import {NodeFormBuilder} from "../../../../../../../shared/models";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {hasDuplicates, required, spaceOnName} from "../../../../../../../shared/utils";
import {newStepValue} from '../../../../../services/flowBuilder/utils/constants';

export const NODE_TYPE = "authenticate";

/** The AuthenticateForm.
 * It is the flattened version of the step.
 * It should contain all the fields that can be changed by the flow-builder.
 */
export interface AuthenticateForm {
    name: string
    menuHeader: string
    imageRef: string
    mandatoryAuthenticate: string
    successfulResponseMessage: string
    authProvider: string
    actionType: string
    nScope: string[]
    responseType: string
    successNext: string
    successDescription: string
    errorNext: string
    errorDescription: string
}

export class Authenticate extends NodeFormBuilder<AuthenticateForm> {

    /**
     * NodeFormBuilder will set up the correct step type.
     */
    constructor() {
        super("menu", NODE_TYPE);
    }

    /**
     * Takes in stepData and returns the flattened version

     * @return AuthenticateForm
     * @param stepData
     */
    getFromData(stepData: { [index: string]: any }): AuthenticateForm {

        const menu = stepData?.menu ? stepData?.menu : {};
        const menuParams = menu?.menuParams ? menu?.menuParams : {};
        const oauth2 = menuParams?.oauth2 ? menuParams?.oauth2 : {};
        const result = menu?.result ? menu?.result : {};

        const successResult = result["success"] ? result["success"] : {};
        const errorResult = result["error"] ? result["error"] : {};

        return {
            name: this.getValueOrDefault(menu?.name),
            menuHeader: this.getValueOrDefault(menu?.name),
            imageRef: this.getValueOrDefault(menu?.name),

            mandatoryAuthenticate: this.getValueOrDefault(menuParams?.mandatoryAuthenticate, false),
            successfulResponseMessage: this.getValueOrDefault(menuParams?.successfulResponseMessage),
            authProvider: this.getValueOrDefault(menuParams?.authProvider),
            actionType: this.getValueOrDefault(menuParams?.actionType, 'authenticate'),

            nScope: this.getValueOrDefault(oauth2?.scope, []),
            responseType: this.getValueOrDefault(oauth2?.responseType),

            successNext: this.getValueOrDefault(successResult?.next, newStepValue),
            successDescription: this.getValueOrDefault(successResult?.description, 'Authentication Successful'),
            errorNext: this.getValueOrDefault(errorResult?.next, newStepValue),
            errorDescription: this.getValueOrDefault(errorResult?.description, 'Authentication Failed'),
        }
    }

    /**
     * Constructs a FormGroup using the passed in FoobarForm
     * Only users with the correct roll is allowed to edit a flow so fields will be locked.
     *
     * @return FormGroup
     * @param formData
     * @param editFlows
     */
    buildForm(formData: AuthenticateForm, editFlows: boolean): FormGroup {

        return new FormGroup({
            name: new FormControl({
                value: formData.name,
                disabled: !editFlows,
            }, [required("Name"), spaceOnName()]),
            menuHeader: new FormControl({
                value: formData.menuHeader,
                disabled: !editFlows,
            }, required("Title")),
            imageRef: new FormControl({
                value: formData.imageRef,
                disabled: !editFlows,
            }),
            mandatoryAuthenticate: new FormControl({
                value: formData.mandatoryAuthenticate,
                disabled: !editFlows,
            }),
            successfulResponseMessage: new FormControl({
                value: formData.successfulResponseMessage,
                disabled: !editFlows,
            }, required("Successful Response Message")),
            authProvider: new FormControl({
                value: formData.authProvider,
                disabled: !editFlows,
            }, required("Auth Provider")),
            actionType: new FormControl({
                value: formData.actionType,
                disabled: !editFlows,
            }),
            nScope: new FormArray(formData.nScope.map(s => new FormControl(s)), [required("Scope"), hasDuplicates()]),
            responseType: new FormControl({
                value: formData.responseType,
                disabled: !editFlows,
            }, [required("Response Type"), spaceOnName("Response Type can`t contain space")]),
            successNext: new FormControl({
                value: formData.successNext,
                disabled: !editFlows,
            }),
            successDescription: new FormControl({
                value: formData.successDescription,
                disabled: !editFlows,
            }, required("Description")),
            errorNext: new FormControl({
                value: formData.errorNext,
                disabled: !editFlows,
            }),
            errorDescription: new FormControl({
                value: formData.errorDescription,
                disabled: !editFlows,
            }, required("Description")),
        })
    }

    /**
     * Reconstructs the step from the passed in AuthenticateForm
     *
     * @param nodeData
     */
    setNodeData(nodeData: AuthenticateForm) {

        const {
            name,
            menuHeader,
            imageRef,
            mandatoryAuthenticate,
            errorDescription,
            successDescription,
            errorNext,
            successNext,
            successfulResponseMessage,
            responseType,
            authProvider,
            nScope,
            actionType
        } = nodeData;

        const menuParams = {
            successfulResponseMessage,
            authProvider,
            mandatoryAuthenticate
        }

        if (actionType === 'authenticate') {
            const oauth2 = {
                scope: nScope,
                responseType
            }
            menuParams['authType'] = "oauth2";
            menuParams['oauth2'] = oauth2;
        }

        const results = {
            success: {
                next: successNext,
                description: successDescription,
            },
            error: {
                next: errorNext,
                description: errorDescription,
            },
        }

        this.menu = {
            ...this.menu,
            name,
            menuHeader,
            imageRef,
            menuParams,
            results
        }
    }

}


// json: {
//   menu: {
//     menuType: 'authenticate',
//     menuHeader: '',
//     name: '',
//     imageRef: '',
//     menuParams: {
//       mandatoryAuthenticate: false,
//       actionType: '',
//       successfulResponseMessage: '',
//       authProvider: '',
//       authType: "oauth2",
//       oauth2: {
//         scope: [],
//         responseType: '',
//       },
//     },
//     results: results,
//   },
//   type: 'menu',
//   metadata: {},
// },


